import { AlertCircle, Plus } from 'lucide-react';

import { RoleBadge } from '@/modules/administration/components/RoleBadge';
import { TenantBadge } from '@/modules/administration/components/TenantBadge';
import type { Role } from '@/modules/auth/roles';
import type { CompanyApi } from '@/modules/companies/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import { TableCell, TableRow } from '@/modules/ui/components/table/Table';
import { TruncatedList } from '@/modules/ui/components/truncated-list/TruncatedList';
import type { UserApi } from '@/modules/users/types';

interface UserTableRowProps {
  user: UserApi;
  companies: CompanyApi[] | undefined;
  onAssignTenant: (user: UserApi) => void;
  onAssignRole: (user: UserApi) => void;
  onManageTenants: (user: UserApi) => void;
  onManageRoles: (user: UserApi) => void;
  onRemoveTenant: (userId: string, tenantId: string) => void;
  onRemoveRole: (userId: string, role: Role) => void;
  onSetDefaultTenant: (userId: string, tenantId: string) => void;
}

export const UserTableRow = ({
  user,
  companies,
  onAssignTenant,
  onAssignRole,
  onManageTenants,
  onManageRoles,
  onRemoveTenant,
  onRemoveRole,
  onSetDefaultTenant,
}: UserTableRowProps) => {
  const sortedTenantIds = user.tenantIds?.slice().sort((a, b) => {
    if (a === user.defaultTenantId) return -1;
    if (b === user.defaultTenantId) return 1;
    return 0;
  });

  return (
    <TableRow>
      <TableCell>
        {user.firstName} {user.lastName}
      </TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          {(!user.tenantIds || user.tenantIds.length === 0) && (
            <Badge variant="destructive">
              <AlertCircle />
              No tenant assigned
            </Badge>
          )}
          <TruncatedList
            items={sortedTenantIds}
            renderItem={(tenantId) => {
              const company = companies?.find((c) => c.id === tenantId);
              const isDefault = user.defaultTenantId === tenantId;

              return (
                <TenantBadge
                  key={tenantId}
                  tenantId={tenantId}
                  user={user}
                  companyName={company?.name || 'Unknown Tenant'}
                  isDefault={isDefault}
                  onSetDefault={onSetDefaultTenant}
                  onRemove={onRemoveTenant}
                />
              );
            }}
            renderMore={(count) => (
              <Badge
                variant="outline"
                className="hover:bg-accent cursor-pointer"
                onClick={() => onManageTenants(user)}
              >
                +{count} more
              </Badge>
            )}
          />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          {(!user.roles || user.roles.length === 0) && (
            <Badge variant="destructive">
              <AlertCircle />
              No role assigned
            </Badge>
          )}
          <TruncatedList
            items={user.roles}
            renderItem={(role) => (
              <RoleBadge
                key={role}
                role={role as Role}
                user={user}
                onRemove={onRemoveRole}
              />
            )}
            renderMore={(count) => (
              <Badge
                variant="outline"
                className="hover:bg-accent cursor-pointer"
                onClick={() => onManageRoles(user)}
              >
                +{count} more
              </Badge>
            )}
          />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onAssignTenant(user)}
          >
            <Plus />
            Assign tenant
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onAssignRole(user)}>
            <Plus />
            Assign role
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};
